import * as React from "react";
import { ListBox } from "../list-box";
import { Flex } from "../flex";
import { cx } from "../../utils/dom";
import { HiSelector } from "react-icons/hi";
import { useListState, useMenuTriggerState } from "react-stately";
import { Key, useMenuTrigger, usePress } from "react-aria";
import { ListBoxProps } from "../list-box/list-box";
import { useListBoxContent } from "../list-box/list-box.utils";
import { Tag } from "../tag";
import { Text } from "../text";
import { InternalPopover as Popover } from "../internal-popover";
import { useTheme } from "../theme/theme-provider";
import { Color } from "../../utils/theme";
import { useSpace } from "../space/space";
import { ID } from "../../utils/types";
import { isArray } from "../../utils/assertions";
import { Field, FieldProps } from "../form/field";

type SingleOrMultipleValue = ID | Array<ID>;
interface MockNativeOnChangeEvent {
  target: {
    value: SingleOrMultipleValue;
    name?: string;
  };
}
export type SelectProps<T> = ListBoxProps<T> &
  FieldProps<
    | "trigger"
    | "listbox"
    | "label"
    | "popover"
    | "description"
    | "errorMessage"
    | "value"
    | "placeholder"
  > & {
    renderValue?: (value: T) => React.ReactNode;
    placeholder?: string;
    isDisabled?: boolean;
    name?: string;
    listBoxColor?: Color;
    value?: SingleOrMultipleValue;
    defaultValue?: SingleOrMultipleValue;
    onChange?(event: MockNativeOnChangeEvent): void;
    onSelectionChange?(keys: Array<Key>): void;
  };

export function Select<T>({
  label,
  selectionMode = "single",
  appearance = "outline",
  renderValue,
  color = "gray",
  error,
  description,
  size = "sm",
  radius,
  listBoxColor,
  className,
  value,
  onChange,
  onSelectionChange,
  style,
  classNames,
  styles,
  showSectionSeparator = true,
  showSelectionIndicator = true,
  ...props
}: SelectProps<T>) {
  const isMultiple = selectionMode === "multiple";

  const _value = React.useMemo(() => {
    if (!value) return null;

    if (selectionMode === "single") {
      return [value];
    }

    if (isMultiple && !isArray(value)) {
      throw new Error(
        "Select: value must be an array when selectionMode is multiple"
      );
    }

    return value as Array<ID>;
  }, [value, selectionMode]);

  const children = useListBoxContent(props);
  const space = useSpace();
  const triggerState = useMenuTriggerState({});
  const listState = useListState({
    ...props,
    selectedKeys: _value,
    defaultSelectedKeys: _value,
    selectionMode: selectionMode,
    children: children,
    onSelectionChange(keys) {
      const keysToArray = [...keys];
      onSelectionChange?.(keysToArray);
      onChange?.({
        target: {
          value: isMultiple ? keysToArray : keysToArray[0],
          name: props.name,
        },
      });

      if (!isMultiple) {
        triggerState.close();
      }
    },
  } as ListBoxProps<any>);
  const triggerRef = React.useRef<HTMLDivElement>(null);

  const theme = useTheme();
  color = color ?? theme?.color;

  const { menuTriggerProps, menuProps } = useMenuTrigger(
    { type: "listbox", trigger: "press", isDisabled: false },
    triggerState,
    triggerRef
  );

  const { pressProps: triggerProps } = usePress(menuTriggerProps);

  const selectedItems = [...listState.selectionManager.selectedKeys].map(
    (item) => listState.collection.getItem(item)
  );

  const content = React.useMemo(() => {
    if (selectedItems.length === 0) {
      return (
        <Text
          style={styles?.placeholder}
          className={classNames?.placeholder}
          color={color}
          variant="lighter"
        >
          {props.placeholder}
        </Text>
      );
    }

    if (isMultiple) {
      return (
        <Flex gap="3xs" wrap="wrap">
          {selectedItems.map(
            (item) =>
              renderValue?.(item?.value) ?? (
                <Tag size="md" variant="pastel" color={color} key={item?.key}>
                  {item?.textValue}
                </Tag>
              )
          )}
        </Flex>
      );
    }

    return (
      <Text
        as="div"
        variant="default"
        color={color === "gray" ? "black" : color}
        className={cx("single-value", classNames?.value)}
        style={styles?.value}
      >
        {renderValue?.(selectedItems[0]?.value) ??
          selectedItems[0]?.value?.[props.labelKey ?? "label"] ??
          selectedItems[0]?.textValue}
      </Text>
    );
  }, [selectedItems.length, isMultiple, selectedItems[0]?.value]);

  return (
    <div
      className={cx("p-select", className, classNames?.wrapper)}
      style={{ ...style, ...styles?.wrapper }}
    >
      {/* implement hidden custom hidden select */}
      <Field
        replaceDefaultControlWrapper
        label={label}
        error={error}
        description={description}
        className={className}
        classNames={classNames}
        size={size}
        radius={radius}
      >
        <div
          style={styles?.trigger}
          data-selection-mode={selectionMode}
          tabIndex={0}
          role="button"
          ref={triggerRef}
          color="gray"
          className={cx(
            "p-input-control",
            "p-select-trigger",
            space.classNames,
            classNames?.trigger
          )}
          data-appearance={appearance}
          {...triggerProps}
        >
          {content}
          <HiSelector className="selector" size={18} />
        </div>
      </Field>
      {triggerState.isOpen && (
        <Popover
          className={classNames?.popover}
          style={styles?.popover}
          placement="bottom start"
          offset={4}
          shouldUpdatePosition
          triggerRef={triggerRef}
          state={triggerState}
        >
          <ListBox
            label={label}
            showSectionSeparator={showSectionSeparator}
            showSelectionIndicator={showSelectionIndicator}
            width={triggerRef.current?.getBoundingClientRect().width}
            color={listBoxColor ?? "gray"}
            shouldFocusOnHover
            state={listState}
            {...menuProps}
            {...props}
          />
        </Popover>
      )}
    </div>
  );
}
